import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthRefresh } from '../lib/useAuthRefresh';

// Harmony Growth API context.
//
// ISOLATION: session keep-alive goes through the shared useAuthRefresh hook
// with apiPrefix '/api/growth'. That hook scopes its axios interceptors by
// URL — it only rewrites Authorization headers and only recovers from 401s
// for calls whose URL contains that prefix. Calls belonging to any other
// Harmony app pass through untouched, so mounting this provider cannot
// affect the shell or any other sub-app sharing the same axios instance.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const API_PREFIX = '/api/growth';

const GrowthContext = createContext(null);

export const useGrowth = () => {
  const ctx = useContext(GrowthContext);
  if (!ctx) throw new Error('useGrowth must be used inside GrowthProvider');
  return ctx;
};

export const GrowthProvider = ({ children, token, role, userName, userId }) => {
  const { tokenRef } = useAuthRefresh(token, {
    backendUrl: BACKEND_URL,
    apiPrefix: API_PREFIX,
  });

  // Every call reads the live token from the ref, so a silent refresh
  // mid-session is picked up on the next request without re-rendering.
  const authCfg = useCallback(
    (extra = {}) => ({
      ...extra,
      headers: { ...(extra.headers || {}), Authorization: `Bearer ${tokenRef.current}` },
    }),
    [tokenRef],
  );

  const get = useCallback(
    async (path, params) => (await axios.get(`${API}/growth${path}`, authCfg({ params }))).data,
    [authCfg],
  );
  const post = useCallback(
    async (path, body) => (await axios.post(`${API}/growth${path}`, body, authCfg())).data,
    [authCfg],
  );
  const put = useCallback(
    async (path, body) => (await axios.put(`${API}/growth${path}`, body, authCfg())).data,
    [authCfg],
  );
  const del = useCallback(
    async (path) => (await axios.delete(`${API}/growth${path}`, authCfg())).data,
    [authCfg],
  );

  const api = useMemo(() => ({
    // dashboard + reference data
    getDashboard:      ()             => get('/dashboard'),
    getResources:      ()             => get('/resources'),
    getCompetencies:   ()             => get('/competencies'),
    saveCompetencies:  (competencies) => put('/competencies', { competencies }),
    getSettings:       ()             => get('/settings'),
    saveSettings:      (body)         => put('/settings', body),

    // notes
    listNotes:         (params)       => get('/notes', params),
    createNote:        (body)         => post('/notes', body),
    updateNote:        (id, body)     => put(`/notes/${id}`, body),
    deleteNote:        (id)           => del(`/notes/${id}`),
    getCoverage:       ()             => get('/notes/coverage'),
    getShortlist:      (from, to)     => get('/notes/quarterly-shortlist', { from_date: from, to_date: to }),

    // probation
    getProbationBoard: ()             => get('/probation/board'),
    enrollProbation:   (body)         => post('/probation', body),
    cancelProbation:   (id)           => del(`/probation/${id}`),
    getReview:         (id)           => get(`/probation/reviews/${id}`),
    completeReview:    (id, body)     => put(`/probation/reviews/${id}`, body),

    // --- Phase 2 ---
    // profiles + rolling anniversary scheduling
    listProfiles:      ()             => get('/profiles'),
    saveProfile:       (uid, body)    => put(`/profiles/${uid}`, body),
    bulkProfiles:      (rows)         => post('/profiles/bulk', { rows }),
    upcomingCycles:    (days = 45)    => get('/profiles/upcoming', { days }),
    generateCycles:    (body = {})    => post('/cycles/generate', body),

    // annual cycle + reviewers
    myAssignments:     ()             => get('/assignments/me'),
    listCycles:        (params)       => get('/cycles', params),
    getCycle:          (id)           => get(`/cycles/${id}`),
    getCycleContext:   (id)           => get(`/cycles/${id}/context`),
    openCycle:         (id)           => post(`/cycles/${id}/open`, {}),
    skipCycle:         (id, reason)   => post(`/cycles/${id}/skip`, { reason }),
    completeCycle:     (id)           => post(`/cycles/${id}/complete`, {}),
    addReviewer:       (id, body)     => post(`/cycles/${id}/reviewers`, body),
    removeReviewer:    (id, aid)      => del(`/cycles/${id}/reviewers/${aid}`),
    submitReview:      (id, body)     => post(`/cycles/${id}/submissions`, body),
    updateSubmission:  (sid, body)    => put(`/submissions/${sid}`, body),
    commitUpload:      (id, body = {})=> post(`/uploads/${id}/commit`, body),

    // self-appraisal (HR side — the resource's own form is a public page)
    inviteAppraisal:   (id, body = {})=> post(`/cycles/${id}/appraisal/invite`, body),
    waiveAppraisal:    (id, body = {})=> post(`/cycles/${id}/appraisal/waive`, body),
    getAppraisal:      (cycleId)      => get(`/appraisals/${cycleId}`),

    // consolidated report, goals, actions
    getReport:         (cycleId)      => get(`/reports/${cycleId}`),
    saveReport:        (cycleId, b)   => put(`/reports/${cycleId}`, b),
    finaliseReport:    (cycleId)      => post(`/reports/${cycleId}/finalise`, {}),
    getGoals:          (cycleId)      => get(`/cycles/${cycleId}/goals`),
    saveGoals:         (cycleId, g)   => put(`/cycles/${cycleId}/goals`, { goals: g }),
    getActions:        (cycleId)      => get(`/cycles/${cycleId}/actions`),
    saveActions:       (cycleId, a)   => put(`/cycles/${cycleId}/actions`, { actions: a }),
  }), [get, post, put, del]);

  // Multipart upload — cannot go through the JSON `post` helper above.
  const uploadReviewerFeedback = useCallback(async (file, cycleId) => {
    const form = new FormData();
    form.append('file', file);
    if (cycleId) form.append('cycle_id', cycleId);
    const r = await axios.post(`${API}/growth/uploads/reviewer-feedback`, form, {
      headers: { Authorization: `Bearer ${tokenRef.current}` },
    });
    return r.data;
  }, [tokenRef]);

  // Word export — blob download, so it also bypasses the JSON helper.
  const exportReport = useCallback(async (cycleId) => {
    const r = await axios.get(`${API}/growth/reports/${cycleId}/export.docx`, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${tokenRef.current}` },
    });
    const cd = r.headers['content-disposition'] || '';
    const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const name = m ? decodeURIComponent(m[1]) : `consolidated-feedback-${cycleId}.docx`;
    const url = URL.createObjectURL(new Blob([r.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return name;
  }, [tokenRef]);

  // Projects come from the Timesheet API — read-only, used to tag notes.
  const listProjects = useCallback(async () => {
    const r = await axios.get(`${API}/timesheet/projects`, {
      headers: { Authorization: `Bearer ${tokenRef.current}` },
    });
    return r.data || [];
  }, [tokenRef]);

  // Admin status comes from the backend, not the role prop. The backend
  // treats `timesheet_role === 'admin'` as admin in addition to the system
  // super_admin / hr_admin roles, so deriving it client-side from `role`
  // alone would hide admin screens from users who genuinely have access.
  // `null` means "not yet known" — the shell renders a loader rather than
  // briefly flashing admin-only tabs at a non-admin.
  const [isAdmin, setIsAdmin] = useState(null);
  const [bootError, setBootError] = useState(null);
  // True when the caller has no general Growth access but DOES hold reviewer
  // assignments. The reviewer pool includes Solutions Architects, Consultants
  // and Project Managers who manage nobody — they must be able to complete
  // the form HR assigned them, and see nothing else in the app.
  const [reviewerOnly, setReviewerOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await get('/dashboard');
        if (!cancelled) setIsAdmin(!!d.is_admin);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status !== 403) {
          setBootError('error');
          setIsAdmin(false);
          return;
        }
        // No general access. Before locking them out, check whether they hold
        // a review assignment — that alone is enough to let them in, limited
        // to the "My reviews" tab.
        try {
          const mine = await get('/assignments/me');
          if (cancelled) return;
          if ((mine?.assignments || []).length > 0) {
            setReviewerOnly(true);
            setIsAdmin(false);
            return;
          }
        } catch (_) { /* fall through to the no-access screen */ }
        if (cancelled) return;
        setBootError('forbidden');
        setIsAdmin(false);
      }
    })();
    return () => { cancelled = true; };
  }, [get]);

  const value = useMemo(
    () => ({
      ...api,
      listProjects,
      uploadReviewerFeedback,
      exportReport,
      role, userName, userId, isAdmin, reviewerOnly, bootError, tokenRef,
    }),
    [api, listProjects, uploadReviewerFeedback, exportReport,
     role, userName, userId, isAdmin, reviewerOnly, bootError, tokenRef],
  );

  return <GrowthContext.Provider value={value}>{children}</GrowthContext.Provider>;
};

export default GrowthContext;
